<template>
  <b-container>
    <b-row class="justify-content-center">
      <b-col md="10" lg="8" v-if="data">
        <b-breadcrumb>
          <b-breadcrumb-item to="/admin">Admin</b-breadcrumb-item>
          <b-breadcrumb-item to="/admin/users">Users</b-breadcrumb-item>
          <b-breadcrumb-item active>Edit User</b-breadcrumb-item>
        </b-breadcrumb>

        <h1>Edit User</h1>

        <b-card class="mb-3">
          <b-form @submit.prevent="updateUser">
            <h2>User Info</h2>

            <user-info-form
              ref="user_form"
              :passwords="false"
              :data="data"
              :errors="errors"
              :loading="loading"
            />

            <div class="d-flex justify-content-end">
              <b-btn
                type="submit"
                variant="secondary"
                size="lg"
                :disabled="loading"
              >
                <span v-if="!loading">Update User Info</span>
                <b-spinner v-if="loading" small></b-spinner>
              </b-btn>
            </div>
          </b-form>
        </b-card>

        <b-card class="mb-3">
          <h2>Admin User</h2>

          <b-btn
            :variant="data.roles == 'admin' ? 'danger' : 'warning'"
            block
            @click="toggleAdmin"
          >
            <span v-if="data.roles == 'admin'">Revoke Admin Access</span>
            <span v-else>Enable Admin Access for User</span>
          </b-btn>
        </b-card>

        <b-card class="mb-3">
          <b-form @submit.prevent="updatePassword">
            <h2>Password</h2>

            <div class="row mx-md-n2">
              <div class="col-sm-6 px-md-2">
                <b-form-group
                  label="New Password"
                  :invalid-feedback="
                    errors.new_password ? errors.new_password[0] : ''
                  "
                  :state="errors.new_password ? false : null"
                >
                  <b-form-input
                    name="new-password"
                    type="password"
                    required
                    trim
                    autocomplete="new-password"
                    v-model="newPassword"
                    :state="errors.new_password ? false : null"
                    :disabled="loading"
                  />
                </b-form-group>
              </div>

              <div class="col-sm-6 px-md-2">
                <b-form-group
                  label="Confirm New Password"
                  :state="errors.new_password ? false : null"
                >
                  <b-form-input
                    name="new-password-confimation"
                    type="password"
                    required
                    trim
                    autocomplete="new-password"
                    v-model="newPasswordConfirmation"
                    :state="errors.new_password ? false : null"
                    :disabled="loading"
                  />
                </b-form-group>
              </div>
            </div>

            <div class="d-flex justify-content-end">
              <b-btn
                type="submit"
                variant="secondary"
                size="lg"
                :disabled="loading"
              >
                <span v-if="!loading">Update Password</span>
                <b-spinner v-if="loading" small></b-spinner>
              </b-btn>
            </div>
          </b-form>
        </b-card>

        <b-card class="mb-3">
          <b-form @submit.prevent="updateCompany">
            <h2>Company Info</h2>

            <company-info-form
              ref="company_form"
              :data="data"
              :errors="errors"
              :loading="loading"
            />

            <div class="d-flex justify-content-end">
              <b-btn
                type="submit"
                variant="secondary"
                size="lg"
                :disabled="loading"
              >
                <span v-if="!loading">Update Company Info</span>
                <b-spinner v-if="loading" small></b-spinner>
              </b-btn>
            </div>
          </b-form>
        </b-card>

        <b-card class="mb-3">
          <h2>Delete User</h2>

          <b-btn variant="danger" block @click="deleteUser">
            Delete User
          </b-btn>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import UserInfoForm from '@/components/users/UserInfoForm'
import CompanyInfoForm from '@/components/users/CompanyInfoForm'

export default {
  name: 'ProfileEditProfile',
  components: {
    UserInfoForm,
    CompanyInfoForm,
  },
  data() {
    return {
      uuid: this.$route.params.uuid,
      data: false,
      errors: {},
      loading: false,
      newPassword: '',
      newPasswordConfirmation: '',
    }
  },
  mounted() {
    this.loadUser()
  },
  methods: {
    loadUser() {
      this.loading = true
      this.errors = {}

      this.$http
        .get(`/admin/users/${this.uuid}`)
        .then(
          (res) => {
            if (res.data.data.expertise && res.data.data.expertise.length > 0) {
              res.data.data.expertise = res.data.data.expertise.map(
                (s) => s.skill_id
              )
            }
            if (
              res.data.data.company_needs &&
              res.data.data.company_needs.length > 0
            ) {
              res.data.data.company_needs = res.data.data.company_needs.map(
                (s) => s.skill_id
              )
            }
            this.data = res.data.data
          },
          (err) => {
            this.errors = err.response.data.errors
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
    updateUser() {
      this.loading = true
      this.errors = {}

      const form = this.$refs.user_form.user
      const formData = new FormData()
      for (let key in form) {
        if (!form[key]) {
          form[key] = ''
        }

        if (key == 'avatar' && form[key] === this.data.avatar) {
          continue
        }

        formData.append(key, form[key])
      }

      this.$http
        .post(`/admin/users/${this.uuid}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(
          () => {
            this.$auth.fetch()
            this.$bvModal.msgBoxOk("This user's profile has been updated.", {
              centered: true,
              title: 'Update User Info',
            })
          },
          (err) => {
            this.errors = err.response.data.errors
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
    updatePassword() {
      this.loading = true
      this.errors = {}

      this.$http
        .post(`/admin/users/${this.uuid}/password`, {
          new_password: this.newPassword,
          new_password_confirmation: this.newPasswordConfirmation,
        })
        .then(
          () => {
            this.$auth.fetch()
            this.oldPassword = this.newPassword = this.newPasswordConfirmation =
              ''

            this.$bvModal.msgBoxOk("This user's password has been updated.", {
              centered: true,
              title: 'Update Password',
            })
          },
          (err) => {
            this.errors = err.response.data.errors
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
    updateCompany() {
      this.loading = true
      this.errors = {}

      this.$http
        .post(
          `/admin/users/${this.uuid}/company`,
          this.$refs.company_form.company
        )
        .then(
          () => {
            this.$auth.fetch()

            this.$bvModal.msgBoxOk(
              "This user's company info has been updated.",
              {
                centered: true,
                title: 'Update Company Info',
              }
            )
          },
          (err) => {
            this.errors = err.response.data.errors
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
    toggleAdmin() {
      let msg =
        'Are you sure you want to enable admin privileges for this user?'
      if (this.data.admin) {
        msg = "Are you sure you want to remove this user's admin privileges?"
      }

      this.$bvModal
        .msgBoxConfirm(msg, {
          centered: true,
          title: 'Admin User',
        })
        .then((value) => {
          if (!value) {
            return
          }

          this.loading = true

          this.$http
            .post(`/admin/users/${this.uuid}/admin`, {})
            .then((res) => {
              this.data = res.data.data
            })
            .finally(() => {
              this.loading = false
            })
        })
    },
    deleteUser() {
      this.$bvModal
        .msgBoxConfirm('Are you sure you want to delete this user?', {
          centered: true,
          title: 'Delete User',
        })
        .then((value) => {
          if (!value) {
            return
          }

          this.$http.delete(`/admin/users/${this.uuid}`).then(() => {
            this.$router.go(-1)
          })
        })
    },
  },
}
</script>
